/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 */

import { BaseSVG } from '../../../shared/BaseSVG';
import { translate } from '../../Internationalization';
import { withProperty } from 'App/Homeowner/PropertyProvider';
import formatAddressToString from '../../../utils/formatAddressToString';
import AllConnectWidget from './AllConnectWidget';
import images from '../../../config/local/images';
import MoveConciergePortal from './MoveConciergePortal';
import React from 'react';

/** @extends {React.Component<Props, State>} */
export class Utilities extends React.Component {
	#bindings = [
		'_handlePropertySelectorClick',
		'_renderPropertyOption',
		'handlePropertyDropdownToggleClick',
		'handlePropertySelect',
	];

	constructor(props) {
		super(props);

		this.propertySelector = React.createRef();
		this.state = {
			isPropertyDropdownActive: false,
		};

		this.#bindings.forEach((binding) => {
			this[binding] = this[binding].bind(this);
		});
	}

	componentDidMount() {
		document.addEventListener('click', this._handlePropertySelectorClick);
	}

	componentWillUnmount() {
		document.removeEventListener(
			'click',
			this._handlePropertySelectorClick
		);
	}

	/**
	 * @param {Event} event
	 * @returns {void}
	 */
	_handlePropertySelectorClick(event) {
		const isClickInside = this.propertySelector.current?.contains(
			event.target
		);

		if (isClickInside) {
			return;
		}

		this.setState({
			isPropertyDropdownActive: false,
		});
	}

	/**
	 * @param {Props['property']['propertyList'][0]} property
	 * @returns {JSX.Element}
	 */
	_renderPropertyOption(property) {
		return (
			<li className="menu-item" key={property.id}>
				<button
					className="link w-full"
					onClick={() => this.handlePropertySelect(property.id)}
					type="button"
				>
					<span>{formatAddressToString(property.address)}</span>
					<span className="action">
						{translate('global.actions.select')}
						<i className="icon icon-sm">
							<BaseSVG path={images.icons.homeowners} />
						</i>
					</span>
				</button>
			</li>
		);
	}

	/**
	 * @returns {void}
	 */
	handlePropertyDropdownToggleClick() {
		this.setState((prevState) => ({
			isPropertyDropdownActive: !prevState.isPropertyDropdownActive,
		}));
	}

	/**
	 * @param {string} id
	 * @returns {void}
	 */
	handlePropertySelect(id) {
		this.props.property.setSelectedProperty(id);
		this.setState({
			isPropertyDropdownActive: false,
		});
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderPropertySelector() {
		if (!this.props.property.propertyList.length) {
			return null;
		}

		if (!this.props.property.selectedProperty?.id) {
			return null;
		}

		const selectedPropertyAddress = formatAddressToString(
			this.props.property.selectedProperty.address
		);
		const menuList = this.props.property.propertyList
			.filter(
				(property) =>
					property.id !== this.props.property.selectedProperty?.id
			)
			.map(this._renderPropertyOption);

		return (
			<section className="fancy-dropdown w-full md:w-auto">
				<button
					aria-controls="utilities-property-selector-drop-down"
					aria-expanded={
						this.state.isPropertyDropdownActive ? 'true' : 'false'
					}
					aria-label={translate(
						'aria_labels.property_drop_down_selector',
						selectedPropertyAddress
					)}
					className={`selector ${
						this.state.isPropertyDropdownActive ? 'active' : null
					}`}
					id="utilities-property-selector-button"
					onClick={this.handlePropertyDropdownToggleClick}
					ref={this.propertySelector}
					type="button"
				>
					<span className="label">
						<img
							alt=""
							aria-hidden="true"
							className="icon"
							src={images.homeowners.header.house}
						/>
						<span className="uppercase">
							{translate('global.header.property')}
						</span>
					</span>
					<span className="current-item">
						<span className="value">{selectedPropertyAddress}</span>
						<i aria-hidden="true" className="icon icon-xs">
							<BaseSVG path={images.homeowners.header.arrow} />
						</i>
					</span>
				</button>
				{menuList.length ? (
					<ul
						aria-labelledby="utilities-property-selector-button"
						className="menu"
						id="utilities-property-selector-drop-down"
						role="region"
					>
						{menuList}
					</ul>
				) : null}
			</section>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		const selectedProperty = this.props.property.selectedProperty;
		const selectedPropertyAddress = selectedProperty?.address
			? formatAddressToString(selectedProperty.address)
			: '';

		return (
			<article className="mx-auto max-w-app">
				<header className="mt-10 mb-6">
					<section className="mb-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
						<h1 className="hl-ms-6">
							{translate('homeowner.pages.utilities.title')}
						</h1>
						{this.#renderPropertySelector()}
					</section>
					<p>
						{translate(
							'homeowner.pages.properties.view.utilities.subtext'
						)}
					</p>
				</header>

				<section className="mb-10">
					<MoveConciergePortal
						address={selectedPropertyAddress}
						propertyId={selectedProperty?.id ?? null}
						withTitle={false}
					/>
				</section>

				<section className="rich-text mb-4">
					<h2>
						{translate(
							'homeowner.pages.properties.view.utilities.allconnect_title'
						)}
					</h2>

					<p>{translate('homeowner.pages.utilities.lead')}</p>
				</section>

				{selectedProperty?.address ? (
					<AllConnectWidget
						address={selectedProperty.address}
						key={selectedProperty.id ?? 'selected-property'}
					/>
				) : null}
			</article>
		);
	}
}

export default withProperty(Utilities);
